import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import client from "@/api/client";
import { invalidateOrganization, registerViaInvite } from "@/api/organization";
import TermsModal from "@/components/TermsModal";
import { setInviteRegistrationInFlight, useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { Input } from "@/components/ui/Input";
import "@/styles/panel-shared.css";

type AuthContextValue = {
  user: { id: number; email: string; name?: string | null } | null;
  isAuthenticated: boolean;
  refreshMe: () => Promise<unknown>;
};

type InvitePreview = {
  organization_name: string;
  email_domain: string | null;
  invited_email: string;
  intended_role: string;
  expired: boolean;
  already_accepted: boolean;
  revoked: boolean;
};

type ApiError = { response?: { status?: number; data?: { detail?: string } } };

// Maps the backend invite-register error contract to user-facing copy:
// 401 missing/invalid bearer, 403 email mismatch, 404 unknown token,
// 409 accepted by another account, 410 expired or revoked.
function describeApiError(err: unknown, fallback: string): string {
  const status = (err as ApiError)?.response?.status;
  const detail = (err as ApiError)?.response?.data?.detail;
  if (status === 401) {
    return detail || "Your session could not be verified. Please try again.";
  }
  if (status === 403) {
    return detail || "This account's email does not match the invited address.";
  }
  if (status === 404) {
    return detail || "This invite link is invalid.";
  }
  if (status === 409) {
    return detail || "This invite has already been accepted by another account.";
  }
  if (status === 410) {
    return detail || "This invite has expired or been revoked.";
  }
  return detail || fallback;
}

// 403 / 409 / 410 mean this Supabase identity can never complete this invite.
// Drop the orphaned Supabase session so it doesn't 401-spam /auth/session on
// every subsequent app boot.
async function signOutIfTerminal(err: unknown): Promise<void> {
  const status = (err as ApiError)?.response?.status;
  if (status === 403 || status === 409 || status === 410) {
    try {
      await supabase.auth.signOut();
    } catch {
      // best-effort
    }
  }
}

export default function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user, refreshMe } = useAuth() as AuthContextValue;

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);

  // Logged-out flow: registration form by default, sign-in form for users who
  // already have a (confirmed) Supabase account for the invited email.
  const [view, setView] = useState<"register" | "signin">("register");
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [autoFinishing, setAutoFinishing] = useState(false);
  const [regName, setRegName] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirm, setRegConfirm] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [regSubmitting, setRegSubmitting] = useState(false);
  const [signinPassword, setSigninPassword] = useState("");
  const [signinSubmitting, setSigninSubmitting] = useState(false);
  const autoAttemptedRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await client.get(`/invites/${encodeURIComponent(token)}`);
        setPreview(res.data);
      } catch {
        setError("This invite link is invalid or has expired.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const inviteInvalid =
    preview && (preview.expired || preview.already_accepted || preview.revoked);

  const emailMismatch =
    isAuthenticated &&
    preview &&
    user?.email &&
    user.email.toLowerCase() !== preview.invited_email.toLowerCase();

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    setError("");
    try {
      await client.post(`/invites/${encodeURIComponent(token)}/accept`);
      // Joining an org changes the license answer — drop the cached (possibly
      // 60s-stale "inactive") /me/organization* entries so LicenseContext
      // refetches instead of bouncing the fresh member to /no-license.
      invalidateOrganization(user);
      await refreshMe();
      navigate("/home", { replace: true });
    } catch (err: unknown) {
      setError(describeApiError(err, "Could not accept invite."));
    } finally {
      setAccepting(false);
    }
  };

  // Calls the (idempotent) register endpoint with a Supabase access token,
  // then mirrors a successful /auth/session: the backend has set the session
  // cookie, so refetch /auth/me and enter the app. Callers own the
  // setInviteRegistrationInFlight() race guard around the *entire* flow
  // (Supabase auth call included) — see AuthContext.jsx.
  const finishInviteRegistration = useCallback(
    async (accessToken: string, name?: string) => {
      if (!token) return;
      await registerViaInvite(token, name ? { name } : {}, accessToken);
      try {
        await refreshMe();
      } catch {
        // Registration itself succeeded — don't tell the user account
        // creation failed. A reload will pick up the fresh session cookie.
        setError(
          "Your account was created, but we couldn't load your session. Please reload the page or sign in again.",
        );
        return;
      }
      navigate("/home", { replace: true });
    },
    [token, refreshMe, navigate],
  );

  // Redirect-back-from-email-confirmation case: the confirmation link lands
  // back on this page with a live Supabase session but (possibly) no backend
  // account yet. If the session email matches the invite, finish registration
  // automatically instead of showing the forms again. autoAttemptedRef makes
  // this single-shot per page load.
  useEffect(() => {
    if (loading || !preview || inviteInvalid || isAuthenticated) return;
    if (autoAttemptedRef.current) return;
    autoAttemptedRef.current = true;

    const run = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const sessionEmail = session?.user?.email;
      if (
        !session?.access_token ||
        !sessionEmail ||
        sessionEmail.toLowerCase() !== preview.invited_email.toLowerCase()
      ) {
        return;
      }
      setAutoFinishing(true);
      setInviteRegistrationInFlight(true);
      try {
        await finishInviteRegistration(session.access_token);
      } catch (err: unknown) {
        setError(describeApiError(err, "Could not finish joining. Please sign in below."));
        await signOutIfTerminal(err);
      } finally {
        setInviteRegistrationInFlight(false);
        setAutoFinishing(false);
      }
    };
    run();
  }, [loading, preview, inviteInvalid, isAuthenticated, finishInviteRegistration]);

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!preview || !token) return;
    setError("");
    if (regPassword !== regConfirm) {
      setError("Password confirmation does not match.");
      return;
    }
    if (!termsAccepted) {
      setError("Please accept the Terms & Conditions to continue.");
      return;
    }
    setRegSubmitting(true);
    // Guard must be raised BEFORE supabase.auth.signUp(): supabase-js emits
    // SIGNED_IN during that call, and AuthContext's listener would otherwise
    // hit /auth/session before the backend account exists (→ 401 → looks
    // like a logout). Cleared only after the whole flow settles.
    setInviteRegistrationInFlight(true);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: preview.invited_email,
        password: regPassword,
        options: {
          emailRedirectTo: `${window.location.origin}/invite/${encodeURIComponent(token)}`,
          data: { full_name: regName.trim() || undefined },
        },
      });
      if (signUpError) throw signUpError;

      if (data.session?.access_token) {
        await finishInviteRegistration(data.session.access_token, regName.trim());
      } else {
        setAwaitingConfirmation(true);
        setView("signin");
      }
    } catch (err: unknown) {
      setError(describeApiError(err, "Could not create your account."));
      await signOutIfTerminal(err);
    } finally {
      setInviteRegistrationInFlight(false);
      setRegSubmitting(false);
    }
  };

  const handleSigninSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!preview || !token) return;
    setError("");
    setSigninSubmitting(true);
    // Same race guard as registration — signInWithPassword also emits
    // SIGNED_IN before the backend account is guaranteed to exist.
    setInviteRegistrationInFlight(true);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: preview.invited_email,
        password: signinPassword,
      });
      if (signInError) throw signInError;
      if (!data.session?.access_token) {
        throw new Error("Could not obtain a session. Please try again.");
      }
      await finishInviteRegistration(data.session.access_token);
    } catch (err: unknown) {
      setError(describeApiError(err, "Could not sign in."));
      await signOutIfTerminal(err);
    } finally {
      setInviteRegistrationInFlight(false);
      setSigninSubmitting(false);
    }
  };

  return (
    <div className="ps-invite-page">
      <section className="ps-section ps-invite-card">
        <div className="ps-section-header">
          <p className="ps-section-title">Organization invite</p>
        </div>
        <div className="ps-section-body">
          {loading && <p className="ps-section-subtitle">Loading invite…</p>}
          {error && !loading && <p className="ps-feedback-row error">{error}</p>}
          {preview && !loading && (
            <>
              <p className="ps-section-subtitle">
                You have been invited to join <strong>{preview.organization_name}</strong>
                {preview.email_domain ? ` (@${preview.email_domain})` : ""}.
              </p>
              <p className="ps-section-subtitle">
                Invited as{" "}
                <strong>{preview.intended_role === "org_admin" ? "Organization admin" : "Member"}</strong>{" "}
                for <strong>{preview.invited_email}</strong>
              </p>
              {inviteInvalid && (
                <p className="ps-feedback-row error">
                  {preview.revoked && "This invite has been revoked."}
                  {preview.expired && "This invite has expired."}
                  {preview.already_accepted && "This invite has already been used."}
                </p>
              )}
              {emailMismatch && (
                <p className="ps-feedback-row error">
                  Sign in as {preview.invited_email} to accept (currently signed in as {user?.email}).
                </p>
              )}
              {!inviteInvalid && isAuthenticated && (
                <button
                  type="button"
                  className="ps-btn-primary"
                  disabled={accepting || Boolean(emailMismatch)}
                  onClick={handleAccept}
                >
                  {accepting ? "Joining…" : "Accept invite"}
                </button>
              )}

              {!inviteInvalid && !isAuthenticated && autoFinishing && (
                <p className="ps-section-subtitle">Finishing your registration…</p>
              )}

              {!inviteInvalid && !isAuthenticated && !autoFinishing && view === "register" && (
                <form className="ps-invite-form" onSubmit={handleRegisterSubmit} noValidate>
                  <div className="ps-field ps-field--full">
                    <label htmlFor="invite-email">Email</label>
                    <Input id="invite-email" className="ps-field-input" value={preview.invited_email} disabled readOnly />
                  </div>
                  <div className="ps-field ps-field--full">
                    <label htmlFor="invite-name">Full name</label>
                    <Input
                      id="invite-name"
                      className="ps-field-input"
                      type="text"
                      autoComplete="name"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      placeholder="Jane Smith"
                      minLength={2}
                      maxLength={120}
                      required
                    />
                  </div>
                  <div className="ps-field">
                    <label htmlFor="invite-password">Password</label>
                    <Input
                      id="invite-password"
                      className="ps-field-input"
                      type="password"
                      autoComplete="new-password"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      minLength={10}
                      required
                    />
                  </div>
                  <div className="ps-field">
                    <label htmlFor="invite-confirm">Confirm password</label>
                    <Input
                      id="invite-confirm"
                      className="ps-field-input"
                      type="password"
                      autoComplete="new-password"
                      value={regConfirm}
                      onChange={(e) => setRegConfirm(e.target.value)}
                      minLength={10}
                      required
                    />
                  </div>
                  <div className="auth-terms-row ps-field--full">
                    <input
                      id="invite-terms"
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                    <label htmlFor="invite-terms">
                      I agree to the{" "}
                      <button type="button" className="auth-terms-link" onClick={() => setTermsOpen(true)}>
                        Terms &amp; Conditions
                      </button>
                    </label>
                  </div>
                  <div className="ps-invite-form-actions ps-field--full">
                    <button type="submit" className="ps-btn-primary" disabled={regSubmitting || !termsAccepted}>
                      {regSubmitting ? "Creating account…" : "Create account & join"}
                    </button>
                  </div>
                  <p className="ps-invite-alt ps-field--full">
                    Already confirmed your email or have a password?{" "}
                    <button
                      type="button"
                      className="auth-terms-link"
                      onClick={() => {
                        setError("");
                        setView("signin");
                      }}
                    >
                      Sign in to finish
                    </button>
                  </p>
                </form>
              )}

              {!inviteInvalid && !isAuthenticated && !autoFinishing && view === "signin" && (
                <>
                  {awaitingConfirmation && (
                    <p className="ps-feedback-row success">
                      Check your email to confirm your address, then sign in below to finish joining{" "}
                      {preview.organization_name}.
                    </p>
                  )}
                  <form className="ps-invite-form" onSubmit={handleSigninSubmit} noValidate>
                    <div className="ps-field ps-field--full">
                      <label htmlFor="invite-signin-email">Email</label>
                      <Input
                        id="invite-signin-email"
                        className="ps-field-input"
                        value={preview.invited_email}
                        disabled
                        readOnly
                      />
                    </div>
                    <div className="ps-field ps-field--full">
                      <label htmlFor="invite-signin-password">Password</label>
                      <Input
                        id="invite-signin-password"
                        className="ps-field-input"
                        type="password"
                        autoComplete="current-password"
                        value={signinPassword}
                        onChange={(e) => setSigninPassword(e.target.value)}
                        required
                      />
                    </div>
                    <div className="ps-invite-form-actions ps-field--full">
                      <button type="submit" className="ps-btn-primary" disabled={signinSubmitting}>
                        {signinSubmitting ? "Signing in…" : "Sign in & join"}
                      </button>
                    </div>
                    <p className="ps-invite-alt ps-field--full">
                      Need to create your account first?{" "}
                      <button
                        type="button"
                        className="auth-terms-link"
                        onClick={() => {
                          setError("");
                          setView("register");
                        }}
                      >
                        Back to registration
                      </button>
                    </p>
                  </form>
                </>
              )}
            </>
          )}
        </div>
        <div className="ps-section-footer">
          <Link to="/" className="ps-btn-secondary ps-invite-back-link">
            Back to home
          </Link>
        </div>
        {termsOpen && <TermsModal onClose={() => setTermsOpen(false)} />}
      </section>
    </div>
  );
}
